import {
  DashboardPage,
  DashboardView,
  DashboardViewConfiguration,
  DASHBOARD_VIEW_TYPE,
  NOTIFICATION_CATEGORY,
  NOTIFICATION_STATUS,
  NotificationPreferences,
  CustomMeasureDefinition,
  CustomMeasureRecord,
  PaginatedNotificationsResponse,
  Pixel,
  PixelEventConfig,
} from '@adtrackify/at-tracking-event-types';
import axios, { AxiosResponse } from 'axios';

import type { NOTIFICATION_BATCH_ACTION } from './types/accounts-api-service-enums';
import type {
  AccountListResponse,
  AccountOnboardingResponse,
  AccountProfileSettings,
  AccountResponse,
  CreateAccountPayload,
  DashboardDefaultViewIds,
  DashboardPagesResponse,
  NotificationConfigResponse,
  NotificationPreferencesResponse,
  SignupPayload,
  SignupResponse,
  UpdateAccountOnboardingPayload,
  UpdateAccountPersonalizationPayload,
  UpdateAccountProfilePayload,
} from './types/accounts-api-service-types';

interface DashboardBootstrapResponse {
  pages: DashboardPage[];
  viewsByType: Record<DASHBOARD_VIEW_TYPE, DashboardView[]>;
  currentViewsByType: Record<DASHBOARD_VIEW_TYPE, DashboardView | null>;
  selectedViewIdsByType: Record<DASHBOARD_VIEW_TYPE, string | null>;
  defaultViewIdsByType?: DashboardDefaultViewIds;
}

import { setupAuthInterceptor } from '../core/auth-interceptor';
import { REQUEST_TIMEOUT_MS } from '../core/constants/request-constants';
import { getAuthorizationConfig } from '../core/auth-config';

const _accountApiClient = axios.create({
  baseURL: import.meta.env.VITE_APP_API_BASE_URL + '/accounts',
  timeout: REQUEST_TIMEOUT_MS,
});

setupAuthInterceptor(_accountApiClient);

/*
dashboardPages:
getDashboardPages - input: pixelId - will return all pages under particular pixelId
getDashboardPage - input: dashboardPageId - will return particular page (if we want to fetch single by id)
createDashboardPage - input: pixelId, userId (will be taken from authorizer), name
updateDashboardPage - input: dashboardPageId, userId (will be taken from authorizer), name, viewId

dashboardViews:
getDashboardViews - input: pixelId - will return all views under particular pixelId (not detailed response - will return id, name, order) - we can make detailed response configurable via an input parameter if this is required
getDashboardView - input: dashboardViewId - return entire dashboardView with config using id
createDashboardView - input: pixelId, userId, name, configuration (as per type), order
updateDashboardView - input: dashboardViewId, userId, name, configuration, order
deleteDashboardView - input: dashboardViewId
copyDashboardView - input: dashboardViewId, userId
wherever userId is input - will be taken from authoriser and required

*/

// dashboardPages: getDashboardPages - GET / accounts / px / { pixelId } / dashboard / pages;
// getDashboardPage - GET / accounts / px / dashboard / pages / { dashboardPageId };
// createDashboardPage - POST / accounts / px / dashboard / pages;
// updateDashboardPage - PATCH / accounts / px / dashboard / pages / { dashboardPageId };
// dashboardViews: getDashboardViews - GET / accounts / px / { pixelId } / dashboard / views;
// getDashboardView - GET / accounts / px / dashboard / views / { dashboardViewId };
// createDashboardView - POST / accounts / px / dashboard / views;
// updateDashboardView - PATCH / accounts / px / dashboard / views / { dashboardViewId };
// deleteDashboardView - DELETE / accounts / px / dashboard / views / { dashboardViewId };
// copyDashboardView - POST / accounts / px / dashboard / views / { dashboardViewId } / copy;

export const AccountsApiService = {
  // Signup method (no auth required)
  signup: async (payload: SignupPayload): Promise<AxiosResponse<SignupResponse>> => {
    return await _accountApiClient.post('/signup', payload);
  },

  // Existing account methods
  getUserAccounts: async (): Promise<AxiosResponse<AccountListResponse>> => {
    const config = await getAuthorizationConfig();
    return await _accountApiClient.get('/current-user', config);
  },

  getTeamMembers: async (accountId: string): Promise<AxiosResponse<{ users: any[] }>> => {
    const config = await getAuthorizationConfig();
    return await _accountApiClient.get(`/${accountId}/users`, config);
  },

  inviteUser: async (
    accountId: string,
    email: string,
    role: string
  ): Promise<AxiosResponse<{ message: string }>> => {
    const config = await getAuthorizationConfig();
    return await _accountApiClient.post(`/${accountId}/invite-user`, { email, role }, config);
  },

  bulkInviteUsers: async (
    accountId: string,
    emails: string[],
    roles: string[]
  ): Promise<AxiosResponse<{ message: string }>> => {
    const config = await getAuthorizationConfig();
    return await _accountApiClient.post(
      `/${accountId}/bulk-invite-users`,
      { emails, roles },
      config
    );
  },

  removeUser: async (
    accountId: string,
    userId: string
  ): Promise<AxiosResponse<{ message: string }>> => {
    const config = await getAuthorizationConfig();
    return await _accountApiClient.post(`/${accountId}/remove-user`, { userId }, config);
  },

  updateUserRole: async (
    accountId: string,
    userId: string,
    role: string
  ): Promise<AxiosResponse<{ message: string }>> => {
    const config = await getAuthorizationConfig();
    return await _accountApiClient.post(`/${accountId}/update-user-role`, { userId, role }, config);
  },

  getAccount: async (id: string | undefined): Promise<AxiosResponse<AccountResponse>> => {
    const config = await getAuthorizationConfig();
    return await _accountApiClient.get(`/${id}`, config);
  },
  updateAccountSettings: async (
    id: string,
    data: { settings: AccountProfileSettings }
  ): Promise<AxiosResponse<AccountResponse>> => {
    const config = await getAuthorizationConfig();
    return await _accountApiClient.patch(`/${id}/profile`, data, config);
  },
  renameAccount: async (
    id: string,
    accountName: string
  ): Promise<AxiosResponse<AccountResponse>> => {
    const config = await getAuthorizationConfig();
    return await _accountApiClient.patch(`/${id}/profile`, { accountName }, config);
  },
  updateAccountProfile: async (
    id: string,
    payload: UpdateAccountProfilePayload
  ): Promise<AxiosResponse<AccountResponse>> => {
    const config = await getAuthorizationConfig();
    return await _accountApiClient.patch(`/${id}/profile`, payload, config);
  },
  createNewContainer: async (
    accountId: string,
    data: { name: string }
  ): Promise<AxiosResponse<Pixel>> => {
    const config = await getAuthorizationConfig();
    return await _accountApiClient.post(`/${accountId}/pixels/`, data, config);
  },

  // Dashboard pages methods
  getDashboardPages: async (pixelId: string): Promise<AxiosResponse<DashboardPagesResponse>> => {
    const config = await getAuthorizationConfig();
    return await _accountApiClient.get(`/px/${pixelId}/dashboard/pages`, config);
  },

  getDashboardPage: async (dashboardPageId: string): Promise<AxiosResponse<DashboardPage>> => {
    const config = await getAuthorizationConfig();
    return await _accountApiClient.get(`/px/dashboard/pages/${dashboardPageId}`, config);
  },

  createDashboardPage: async (
    pixelId: string,
    name: string
  ): Promise<AxiosResponse<{ dashboardPage: DashboardPage }>> => {
    const config = await getAuthorizationConfig();
    return await _accountApiClient.post(
      '/px/dashboard/pages',
      {
        pixelId,
        name,
      },
      config
    );
  },

  updateDashboardPage: async (
    dashboardPageId: string,
    name: string,
    viewId: string
  ): Promise<AxiosResponse<{ dashboardPage: DashboardPage }>> => {
    const config = await getAuthorizationConfig();
    return await _accountApiClient.patch(
      `/px/dashboard/pages/${dashboardPageId}`,
      {
        dashboardPageId,
        name,
        viewId,
      },
      config
    );
  },

  // Dashboard views methods
  getDashboardViews: async (
    pixelId: string,
    type?: string
  ): Promise<AxiosResponse<{ dashboardViews: DashboardView[] }>> => {
    const config = await getAuthorizationConfig();
    return await _accountApiClient.get(
      `/px/${pixelId}/dashboard/views?type=${type || 'view'}`,
      config
    );
  },

  getDashboardView: async (
    dashboardViewId: string,
    type?: string
  ): Promise<AxiosResponse<{ dashboardView: DashboardView }>> => {
    const config = await getAuthorizationConfig();
    return await _accountApiClient.get(
      `/px/dashboard/views/${dashboardViewId}?type=${type || 'view'}`,
      config
    );
  },

  getDashboardBootstrap: async (
    pixelId: string,
    viewId?: string,
    type?: DASHBOARD_VIEW_TYPE
  ): Promise<AxiosResponse<DashboardBootstrapResponse>> => {
    const config = await getAuthorizationConfig();
    const params = new URLSearchParams();
    if (type) params.append('type', type);
    if (viewId) params.append('viewId', viewId);
    const qs = params.toString();

    return await _accountApiClient.get(
      `/px/${pixelId}/dashboard/bootstrap${qs ? `?${qs}` : ''}`,
      config
    );
  },

  createDashboardView: async (
    pixelId: string,
    name: string,
    configuration: DashboardViewConfiguration,
    order?: number,
    type = 'view'
  ): Promise<AxiosResponse<{ dashboardView: DashboardView }>> => {
    const config = await getAuthorizationConfig();
    return await _accountApiClient.post(
      '/px/dashboard/views',
      {
        pixelId,
        name,
        configuration,
        order,
        type,
      },
      config
    );
  },

  updateDashboardView: async (
    dashboardViewId: string,
    name: string,
    configuration: DashboardViewConfiguration,
    order?: number,
    type?: string
  ): Promise<AxiosResponse<{ dashboardView: DashboardView }>> => {
    const config = await getAuthorizationConfig();
    return await _accountApiClient.patch(
      `/px/dashboard/views/${dashboardViewId}`,
      {
        dashboardViewId,
        name,
        configuration,
        order,
        type,
      },
      config
    );
  },

  deleteDashboardView: async (dashboardViewId: string): Promise<AxiosResponse<void>> => {
    const config = await getAuthorizationConfig();
    return await _accountApiClient.delete(`/px/dashboard/views/${dashboardViewId}`, config);
  },

  deleteDashboardPage: async (dashboardPageId: string): Promise<AxiosResponse<void>> => {
    const config = await getAuthorizationConfig();
    return await _accountApiClient.delete(`/px/dashboard/pages/${dashboardPageId}`, config);
  },

  copyDashboardView: async (dashboardViewId: string): Promise<AxiosResponse<DashboardView>> => {
    const config = await getAuthorizationConfig();
    return await _accountApiClient.post(`/px/dashboard/views/${dashboardViewId}/copy`, {}, config);
  },

  updateDashboardDefaults: async (
    pixelId: string,
    type: DASHBOARD_VIEW_TYPE,
    viewId: string
  ): Promise<AxiosResponse<{ defaultViewIdsByType: DashboardDefaultViewIds }>> => {
    const config = await getAuthorizationConfig();
    return await _accountApiClient.patch(
      `/px/${pixelId}/dashboard/defaults`,
      { type, viewId },
      config
    );
  },

  verifyUserInvitationToken: async (token: string): Promise<AxiosResponse<{ email: string }>> => {
    return await _accountApiClient.post('/verify-user-invitation-token', { token });
  },

  acceptUserInvitation: async (
    token: string,
    password: string
  ): Promise<AxiosResponse<{ message: string }>> => {
    return await _accountApiClient.post('/accept-user-invitation', { token, password });
  },

  // Create account (company)
  createAccount: async (payload: CreateAccountPayload): Promise<AxiosResponse<AccountResponse>> => {
    const config = await getAuthorizationConfig();
    return await _accountApiClient.post('/', payload, config);
  },

  // Update account personalization
  updateAccountPersonalization: async (
    accountId: string,
    payload: UpdateAccountPersonalizationPayload
  ): Promise<AxiosResponse<AccountResponse>> => {
    const config = await getAuthorizationConfig();
    return await _accountApiClient.patch(`/${accountId}/personalization`, payload, config);
  },

  // Update account onboarding state
  updateAccountOnboarding: async (
    accountId: string,
    payload: UpdateAccountOnboardingPayload
  ): Promise<AxiosResponse<AccountOnboardingResponse>> => {
    const config = await getAuthorizationConfig();
    return await _accountApiClient.patch(`/${accountId}/onboarding`, payload, config);
  },

  // Notification preferences
  getNotificationConfig: async (): Promise<AxiosResponse<NotificationConfigResponse>> => {
    const config = await getAuthorizationConfig();
    return await _accountApiClient.get('/notification-config', config);
  },

  getUserNotificationPreferences: async (
    accountId: string,
    userId: string
  ): Promise<AxiosResponse<NotificationPreferencesResponse>> => {
    const config = await getAuthorizationConfig();
    return await _accountApiClient.get(
      `/${accountId}/users/${userId}/notification-preferences`,
      config
    );
  },

  updateUserNotificationPreferences: async (
    accountId: string,
    userId: string,
    preferences: Partial<NotificationPreferences>
  ): Promise<AxiosResponse<NotificationPreferencesResponse>> => {
    const config = await getAuthorizationConfig();
    return await _accountApiClient.put(
      `/${accountId}/users/${userId}/notification-preferences`,
      preferences,
      config
    );
  },

  // --- Notification Center ---

  getNotifications: async (
    accountId: string,
    params?: { limit?: number; category?: NOTIFICATION_CATEGORY; status?: NOTIFICATION_STATUS; cursor?: string }
  ): Promise<AxiosResponse<PaginatedNotificationsResponse>> => {
    const config = await getAuthorizationConfig();
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.category) queryParams.append('category', params.category);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.cursor) queryParams.append('cursor', params.cursor);
    const qs = queryParams.toString();
    return await _accountApiClient.get(`/${accountId}/notifications${qs ? `?${qs}` : ''}`, config);
  },

  getNotificationUnreadCount: async (
    accountId: string
  ): Promise<AxiosResponse<{ count: number }>> => {
    const config = await getAuthorizationConfig();
    return await _accountApiClient.get(`/${accountId}/notifications/unread-count`, config);
  },

  markNotificationRead: async (
    accountId: string,
    notificationId: string
  ): Promise<AxiosResponse<{ success: boolean }>> => {
    const config = await getAuthorizationConfig();
    return await _accountApiClient.post(`/${accountId}/notifications/${notificationId}/read`, {}, config);
  },

  markAllNotificationsRead: async (
    accountId: string
  ): Promise<AxiosResponse<{ updatedCount: number }>> => {
    const config = await getAuthorizationConfig();
    return await _accountApiClient.post(`/${accountId}/notifications/mark-all-read`, {}, config);
  },

  dismissAllNotifications: async (
    accountId: string,
    filters?: { category?: string; status?: string }
  ): Promise<AxiosResponse<{ dismissedCount: number }>> => {
    const config = await getAuthorizationConfig();
    return await _accountApiClient.post(`/${accountId}/notifications/dismiss-all`, filters ?? {}, config);
  },

  batchNotificationAction: async (
    accountId: string,
    action: NOTIFICATION_BATCH_ACTION,
    notificationIds: string[]
  ): Promise<AxiosResponse<{ processedCount: number }>> => {
    const config = await getAuthorizationConfig();
    return await _accountApiClient.post(`/${accountId}/notifications/batch`, { action, notificationIds }, config);
  },

  deleteAllNotifications: async (
    accountId: string,
    filters?: { category?: string; status?: string }
  ): Promise<AxiosResponse<{ deletedCount: number }>> => {
    const config = await getAuthorizationConfig();
    return await _accountApiClient.post(`/${accountId}/notifications/delete-all`, filters ?? {}, config);
  },

  // --- Pixel Event Config (Global Custom Event Mappings) ---

  getPixelEventConfig: async (
    pixelId: string
  ): Promise<AxiosResponse<{ eventConfig: PixelEventConfig }>> => {
    const config = await getAuthorizationConfig();
    return await _accountApiClient.get(`/px/${pixelId}/event-config`, config);
  },

  // --- API Keys ---

  listApiKeys: async (pixelId: string) => {
    const config = await getAuthorizationConfig();
    return await _accountApiClient.post(`/px/${pixelId}/api-keys`, { action: 'list' }, config);
  },

  createApiKey: async (pixelId: string, payload: { name: string; scopes: string[]; description?: string }) => {
    const config = await getAuthorizationConfig();
    return await _accountApiClient.post(`/px/${pixelId}/api-keys`, { action: 'create', ...payload }, config);
  },

  deleteApiKey: async (pixelId: string, apiKeyPk: string) => {
    const config = await getAuthorizationConfig();
    return await _accountApiClient.post(`/px/${pixelId}/api-keys`, { action: 'delete', apiKeyPk }, config);
  },

  updatePixelEventConfig: async (
    pixelId: string,
    eventConfig: PixelEventConfig
  ): Promise<AxiosResponse<{ eventConfig: PixelEventConfig }>> => {
    const config = await getAuthorizationConfig();
    return await _accountApiClient.put(`/px/${pixelId}/event-config`, eventConfig, config);
  },

  listCustomMeasures: async (
    pixelId: string,
    params?: { pageSize?: number; cursor?: string }
  ): Promise<AxiosResponse<{ customMeasures: CustomMeasureRecord[]; nextCursor?: string }>> => {
    const config = await getAuthorizationConfig({ params });
    return await _accountApiClient.get(`/px/${pixelId}/measures`, config);
  },

  createCustomMeasure: async (
    pixelId: string,
    definition: CustomMeasureDefinition
  ): Promise<AxiosResponse<{ customMeasure: CustomMeasureRecord }>> => {
    const config = await getAuthorizationConfig();
    return await _accountApiClient.post(`/px/${pixelId}/measures`, definition, config);
  },

  updateCustomMeasure: async (
    pixelId: string,
    measureId: string,
    payload: CustomMeasureDefinition & { enabled?: boolean }
  ): Promise<AxiosResponse<{ customMeasure: CustomMeasureRecord }>> => {
    const config = await getAuthorizationConfig();
    return await _accountApiClient.put(`/px/${pixelId}/measures/${measureId}`, payload, config);
  },

  deleteCustomMeasure: async (
    pixelId: string,
    measureId: string
  ): Promise<AxiosResponse<{ customMeasure: CustomMeasureRecord }>> => {
    const config = await getAuthorizationConfig();
    return await _accountApiClient.delete(`/px/${pixelId}/measures/${measureId}`, config);
  },
};
